import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import type {AlertItem, SubscriptionItem, CreateFromEventPayload} from '../../api/alerts';

export type AlertsState = {
  alerts: AlertItem[];
  subs: SubscriptionItem[];

  loadingAlerts: boolean;
  loadingSubs: boolean;

  deletingAlerts: Record<string, boolean>;

  deactivatingSubs: Record<string, boolean>;
  restartingSubs: Record<string, boolean>;
  removingSubs: Record<string, boolean>;

  creatingFlags: Record<string, boolean>;
  upsertingSubs: Record<string, boolean>;
  creatingTickerSubs: Record<string, boolean>;

  error?: string | null;
};

const initialState: AlertsState = {
  alerts: [],
  subs: [],
  loadingAlerts: false,
  loadingSubs: false,
  deletingAlerts: {},
  deactivatingSubs: {},
  restartingSubs: {},
  removingSubs: {},
  creatingFlags: {},
  upsertingSubs: {},
  creatingTickerSubs: {},
  error: null,
};

const keyForSub = (ticker: string, channel: string) =>
    `${String(ticker).toUpperCase()}::${channel}`;

const keyForBulk = (sourceEventId?: string, channel?: string) =>
    `BULK::${String(sourceEventId ?? 'manual')}::${String(channel ?? 'telegram')}`;

function getAlertIdentity(alert: AlertItem, index: number): string {
  const row = alert as any;

  if (row?.id != null && String(row.id).trim()) {
    return `ID::${String(row.id).trim()}`;
  }

  const eventId = row?.eventId ?? row?.event_id ?? row?.sourceEventId ?? row?.source_event_id;
  if (eventId != null && String(eventId).trim()) {
    return `EVENT::${String(eventId).trim()}`;
  }

  const name = String(row?.name ?? '').trim();
  if (name) return `NAME::${name}`;

  const title = String(row?.title ?? '').trim();
  const createdAt = String(row?.createdAt ?? row?.created_at ?? '').trim();
  const sector = String(row?.sector ?? '').trim();
  if (title || createdAt || sector) {
    return `FALLBACK::${title}::${createdAt}::${sector}`;
  }

  return `INDEX::${index}`;
}

function deduplicateAlerts(rows: AlertItem[] | null | undefined): AlertItem[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const unique = new Map<string, AlertItem>();
  rows.forEach((row, index) => {
    if (!row) return;
    const key = getAlertIdentity(row, index);

    if (!unique.has(key)) {
      unique.set(key, row);
      return;
    }

    const previous = unique.get(key) as any;
    const current = row as any;
    const previousTime = new Date(
        previous?.updatedAt ?? previous?.updated_at ?? previous?.createdAt ?? previous?.created_at ?? 0,
    ).getTime();
    const currentTime = new Date(
        current?.updatedAt ?? current?.updated_at ?? current?.createdAt ?? current?.created_at ?? 0,
    ).getTime();

    if (Number.isFinite(currentTime) && (!Number.isFinite(previousTime) || currentTime >= previousTime)) {
      unique.set(key, row);
    }
  });

  return Array.from(unique.values());
}

function deduplicateSubscriptions(rows: SubscriptionItem[] | null | undefined): SubscriptionItem[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const unique = new Map<string, SubscriptionItem>();
  rows.forEach((row, index) => {
    if (!row) return;
    const item = row as any;
    const key = item?.id != null && String(item.id).trim()
        ? `ID::${String(item.id).trim()}`
        : `SUB::${String(item?.ticker ?? '').toUpperCase()}::${String(item?.channel ?? 'telegram')}::${index}`;
    if (!unique.has(key)) unique.set(key, row);
  });

  return Array.from(unique.values());
}

const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    fetchAlertsRequested(state) {
      state.loadingAlerts = true;
      state.error = null;
    },
    fetchAlertsSucceeded(state, action: PayloadAction<AlertItem[]>) {
      state.loadingAlerts = false;
      state.alerts = deduplicateAlerts(action.payload);
    },
    fetchAlertsFailed(state, action: PayloadAction<string>) {
      state.loadingAlerts = false;
      state.error = action.payload;
    },

    fetchSubsRequested(state) {
      state.loadingSubs = true;
      state.error = null;
    },
    fetchSubsSucceeded(state, action: PayloadAction<SubscriptionItem[]>) {
      state.loadingSubs = false;
      state.subs = deduplicateSubscriptions(action.payload);
    },
    fetchSubsFailed(state, action: PayloadAction<string>) {
      state.loadingSubs = false;
      state.error = action.payload;
    },

    deleteAlertRequested(state, action: PayloadAction<{ id: number; userId?: string | number }>) {
      state.deletingAlerts[String(action.payload.id)] = true;
      state.error = null;
    },
    deleteAlertSucceeded(state, action: PayloadAction<number>) {
      delete state.deletingAlerts[String(action.payload)];
      state.alerts = state.alerts.filter((a) => Number((a as any)?.id) !== action.payload);
    },
    deleteAlertFailed(state, action: PayloadAction<{ id: number; message: string }>) {
      delete state.deletingAlerts[String(action.payload.id)];
      state.error = action.payload.message;
    },

    deactivateSubRequested(state, action: PayloadAction<{ id: number; userId?: string | number }>) {
      state.deactivatingSubs[String(action.payload.id)] = true;
      state.error = null;
    },
    deactivateSubSucceeded(state, action: PayloadAction<number>) {
      delete state.deactivatingSubs[String(action.payload)];
      state.subs = state.subs.map((s) =>
          Number((s as any)?.id) === action.payload ? {...s, isActive: false} : s,
      );
    },
    deactivateSubFailed(state, action: PayloadAction<{ id: number; message: string }>) {
      delete state.deactivatingSubs[String(action.payload.id)];
      state.error = action.payload.message;
    },

    reactivateSubRequested(state, action: PayloadAction<{ id: number; userId?: string | number }>) {
      state.restartingSubs[String(action.payload.id)] = true;
      state.error = null;
    },
    reactivateSubSucceeded(state, action: PayloadAction<number>) {
      delete state.restartingSubs[String(action.payload)];
      state.subs = state.subs.map((s) =>
          Number((s as any)?.id) === action.payload ? {...s, isActive: true} : s,
      );
    },
    reactivateSubFailed(state, action: PayloadAction<{ id: number; message: string }>) {
      delete state.restartingSubs[String(action.payload.id)];
      state.error = action.payload.message;
    },

    deleteSubRequested(state, action: PayloadAction<{ id: number; userId?: string | number }>) {
      state.removingSubs[String(action.payload.id)] = true;
      state.error = null;
    },
    deleteSubSucceeded(state, action: PayloadAction<number>) {
      delete state.removingSubs[String(action.payload)];
      state.subs = state.subs.filter((s) => Number((s as any)?.id) !== action.payload);
    },
    deleteSubFailed(state, action: PayloadAction<{ id: number; message: string }>) {
      delete state.removingSubs[String(action.payload.id)];
      state.error = action.payload.message;
    },

    createFromEventRequested(
        state,
        action: PayloadAction<CreateFromEventPayload & {
          userId?: string | number;
          telegramChatId?: string
        }>,
    ) {
      const key = String(action.payload.eventId ?? '');
      if (key) state.creatingFlags[key] = true;
      state.error = null;
    },
    createFromEventSucceeded(state, action: PayloadAction<{ eventId?: string } | undefined>) {
      const eventId = String(action.payload?.eventId ?? '');
      if (eventId) delete state.creatingFlags[eventId];
      else state.creatingFlags = {};
    },
    createFromEventFailed(state, action: PayloadAction<{ eventId: string; message: string }>) {
      const key = String(action.payload?.eventId ?? '');
      if (key) delete state.creatingFlags[key];
      state.error = action.payload.message;
    },

    upsertSubRequested(
        state,
        action: PayloadAction<{
          ticker: string;
          channel: 'telegram';
          endpointId: string;
          userId?: string | number;
        }>,
    ) {
      const {ticker, channel} = action.payload;
      state.upsertingSubs[keyForSub(ticker, channel)] = true;
      state.error = null;
    },
    upsertSubSucceeded(state, action: PayloadAction<{ ticker: string; channel: 'telegram' }>) {
      delete state.upsertingSubs[keyForSub(action.payload.ticker, action.payload.channel)];
    },
    upsertSubFailed(
        state,
        action: PayloadAction<{ ticker: string; channel: 'telegram'; message: string }>,
    ) {
      delete state.upsertingSubs[keyForSub(action.payload.ticker, action.payload.channel)];
      state.error = action.payload.message;
    },

    createTickerSubsRequested(
        state,
        action: PayloadAction<{
          tickers: string[];
          channel: 'telegram';
          endpointId: string;
          sourceEventId?: string;
          userId?: string | number;
        }>,
    ) {
      state.creatingTickerSubs[keyForBulk(action.payload.sourceEventId, action.payload.channel)] = true;
      state.error = null;
    },
    createTickerSubsSucceeded(
        state,
        action: PayloadAction<{ sourceEventId?: string; channel: 'telegram' }>,
    ) {
      delete state.creatingTickerSubs[keyForBulk(action.payload.sourceEventId, action.payload.channel)];
    },
    createTickerSubsFailed(
        state,
        action: PayloadAction<{ sourceEventId?: string; channel: 'telegram'; message: string }>,
    ) {
      delete state.creatingTickerSubs[keyForBulk(action.payload.sourceEventId, action.payload.channel)];
      state.error = action.payload.message;
    },
  },
});

export const {
  fetchAlertsRequested,
  fetchAlertsSucceeded,
  fetchAlertsFailed,
  fetchSubsRequested,
  fetchSubsSucceeded,
  fetchSubsFailed,
  deleteAlertRequested,
  deleteAlertSucceeded,
  deleteAlertFailed,
  deactivateSubRequested,
  deactivateSubSucceeded,
  deactivateSubFailed,
  reactivateSubRequested,
  reactivateSubSucceeded,
  reactivateSubFailed,
  deleteSubRequested,
  deleteSubSucceeded,
  deleteSubFailed,
  createFromEventRequested,
  createFromEventSucceeded,
  createFromEventFailed,
  upsertSubRequested,
  upsertSubSucceeded,
  upsertSubFailed,
  createTickerSubsRequested,
  createTickerSubsSucceeded,
  createTickerSubsFailed,
} = alertsSlice.actions;

export default alertsSlice.reducer;
